import {
  CoffeePhomaAlert,
  CoffeePhomaAlertResponse,
  CoffeePhomaAlertPaginatedResponse,
  CoffeePhomaAlertSummary,
  PhomaCalculationInput,
} from '../models/coffee-phoma-alert';

export class CoffeePhomaAlertAdapter {
  /**
   * Converte resposta da API para CoffeePhomaAlert
   */
  static fromApi(data: any): CoffeePhomaAlert {
    return {
      id: Number(data.id) || 0,
      sadModeloId: Number(data.sadModeloId) || 0,
      weatherStationId: Number(data.weatherStationId) || 0,
      noticeDate: data.noticeDate || '',
      incidenceDate: data.incidenceDate || '',
      value: Number(data.value) || 0,
      dataCount: Number(data.dataCount) || 0,
      riskClassification: data.riskClassification || '',
      calculationInput: this.fromApiCalculationInput(data.calculationInput),
    };
  }

  /**
   * Converte dados de entrada de cálculo da API (suporta PHOMA_RP, PHOMA_SS, PHOMA_NR, PHOMA_MSM)
   */
  static fromApiCalculationInput(data: any): PhomaCalculationInput {
    return {
      type: data?.type || '',
      id: Number(data?.id) || 0,
      weatherStationId: Number(data?.weatherStationId) || 0,
      referenceDate: data?.referenceDate || '',
      dataCount: Number(data?.dataCount) || 0,
      tempMin: data?.tempMin ?? null,
      temp: data?.temp ?? null,
      tempMax: data?.tempMax ?? null,
      humidity: data?.humidity ?? null,
      humidityMax: data?.humidityMax ?? null,
      humidityMin: data?.humidityMin ?? null,
      precipitation: data?.precipitation ?? null,
      solarRadiation: data?.solarRadiation ?? null,
      windSpeed: data?.windSpeed ?? null,
      dewPoint: data?.dewPoint ?? null,
      leafWetness: data?.leafWetness ?? null,
    };
  }

  /**
   * Converte array de dados da API para array de CoffeePhomaAlert
   */
  static fromApiArray(dataArray: any[]): CoffeePhomaAlert[] {
    if (!Array.isArray(dataArray)) {
      return [];
    }
    return dataArray.map((data) => this.fromApi(data));
  }

  /**
   * Converte resposta paginada para CoffeePhomaAlertResponse (usado pelo dashboard)
   * Mapeia totalElements como contagem de alertas favoráveis
   */
  static fromApiResponse(data: any): CoffeePhomaAlertResponse {
    const content = this.fromApiArray(data.content || []);
    return {
      favorableAndVeryFavorableAlertsCount: Number(data.totalElements) || 0,
      nonFavorableAlertsCount: 0,
      nonFavorableAlerts: [],
      favorableAndVeryFavorableAlerts: content,
    };
  }

  /**
   * Converte resposta paginada da API
   */
  static fromApiPaginatedResponse(
    data: any,
  ): CoffeePhomaAlertPaginatedResponse {
    return {
      content: this.fromApiArray(data.content || []),
      totalElements: Number(data.totalElements) || 0,
      totalPages: Number(data.totalPages) || 0,
      currentPage: Number(data.currentPage) || 0,
      hasNext: Boolean(data.hasNext),
      hasPrevious: Boolean(data.hasPrevious),
    };
  }

  /**
   * Cria resumo a partir da resposta da API
   */
  static createSummary(
    response: CoffeePhomaAlertResponse,
  ): CoffeePhomaAlertSummary {
    return {
      totalAlerts:
        response.favorableAndVeryFavorableAlertsCount +
        response.nonFavorableAlertsCount,
      favorableCount: response.favorableAndVeryFavorableAlertsCount,
      nonFavorableCount: response.nonFavorableAlertsCount,
      lastUpdate: new Date().toISOString(),
    };
  }

  /**
   * Mapeia classificação de risco para cor/classe CSS
   */
  static getRiskClassificationClass(riskClassification: string): string {
    const classMap: { [key: string]: string } = {
      'não favorável': 'danger',
      'nao favoravel': 'danger',
      favorável: 'warning',
      favoravel: 'warning',
      'muito favorável': 'success',
      'muito favoravel': 'success',
    };
    return classMap[riskClassification.toLowerCase()] || 'secondary';
  }

  /**
   * Obtém temperatura mínima do alerta (disponível em PHOMA_RP e PHOMA_NR)
   */
  static getTempMin(alert: CoffeePhomaAlert): number | null {
    return alert.calculationInput?.tempMin ?? null;
  }

  /**
   * Obtém temperatura média do alerta (disponível em PHOMA_SS, PHOMA_NR e PHOMA_MSM)
   */
  static getTemp(alert: CoffeePhomaAlert): number | null {
    return alert.calculationInput?.temp ?? null;
  }

  /**
   * Obtém umidade relativa do alerta (disponível em PHOMA_RP)
   */
  static getHumidity(alert: CoffeePhomaAlert): number | null {
    return alert.calculationInput?.humidity ?? null;
  }

  /**
   * Obtém ponto de orvalho do alerta (disponível em PHOMA_RP, PHOMA_SS e PHOMA_MSM)
   */
  static getDewPoint(alert: CoffeePhomaAlert): number | null {
    return alert.calculationInput?.dewPoint ?? null;
  }
}
